/**
 * Backend i18n kataloğunu (düz "a.b.c" → metin) vue-i18n mesaj ağacına yazar / ağaçtan siler.
 *
 * Önek çakışması: katalogda hem "x.y" (metin) hem "x.y.info" (alt anahtar) bulunabilir. İç içe ağaçta
 * ikisi aynı anda tutulamaz; eski kod üst metni nesneye çevirip "x.y" çağrısını ham anahtara düşürüyordu
 * (backend HashMap döndüğü için hangisinin kazandığı rastgeleydi). vue-i18n 9 kökteki noktalı düz
 * anahtarları da çözer ama iç içe değer bulunursa onu tercih eder. Bu yüzden:
 *   - üst metin iç içe kalır, çakışan alt anahtar köke düz yazılır ("x.y.info": "...");
 *   - yazım sırası kısa anahtardan uzuna olmalı (applyCatalog bunu sağlar).
 */
type MessageTree = Record<string, any>

const isTree = (v: unknown): v is MessageTree =>
    typeof v === 'object' && v !== null && !Array.isArray(v)

/** Ağaçtaki tüm yaprakları köke düz anahtar olarak taşır (prefix + '.' + yol). */
function hoistLeaves(root: MessageTree, subtree: MessageTree, prefix: string) {
    for (const [key, value] of Object.entries(subtree)) {
        const path = `${prefix}.${key}`
        if (isTree(value)) hoistLeaves(root, value, path)
        else root[path] = value
    }
}

export function setMessage(root: MessageTree, path: string, value: string) {
    const segments = path.split('.')
    let current = root

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index]!
        const next = current[segment]
        if (typeof next === 'string') {
            // Üst yol zaten bir metin: onu ezmek yerine alt anahtarı köke düz yaz.
            root[path] = value
            return
        }
        if (!isTree(next)) current[segment] = {}
        current = current[segment]
    }

    const last = segments[segments.length - 1]!
    if (isTree(current[last])) {
        // Bu yolda alt anahtarlar var (ör. yerel dosyadan): onları köke taşı, üst metni yerine yaz.
        hoistLeaves(root, current[last], path)
    }
    current[last] = value
    // Daha önce köke düz yazılmış aynı anahtar kalmasın (tek parçalı anahtarda root[path] az önce yazılan değerdir).
    if (segments.length > 1) delete root[path]
}

export function deleteMessage(root: MessageTree, path: string) {
    if (path.includes('.') && Object.prototype.hasOwnProperty.call(root, path)) {
        delete root[path]
    }

    const segments = path.split('.')
    const stack: Array<{ parent: MessageTree, key: string }> = []
    let current: unknown = root

    for (const segment of segments) {
        if (!isTree(current)) return
        stack.push({ parent: current, key: segment })
        current = current[segment]
    }
    if (current === undefined || isTree(current)) return

    for (let index = stack.length - 1; index >= 0; index -= 1) {
        const { parent, key } = stack[index]!
        delete parent[key]
        if (Object.keys(parent).length > 0) break
    }
}

/** Kataloğu ağaca uygular: önce önceki kataloğun anahtarlarını siler, sonra yenileri kısa→uzun yazar. */
export function applyCatalog(
    root: MessageTree,
    next: Record<string, string>,
    previous?: Record<string, string> | null
) {
    if (previous) {
        for (const key of Object.keys(previous)) deleteMessage(root, key)
    }
    const keys = Object.keys(next).sort((a, b) => a.split('.').length - b.split('.').length || (a < b ? -1 : a > b ? 1 : 0))
    for (const key of keys) setMessage(root, key, next[key]!)
    return root
}
